import {useEffect, useMemo, useState} from 'react';

import {translations} from 'presentation/translations-new';

import {useConsultRefundV2} from 'presentation/pages/private/refund/hooks';

import {
	CONSULT_REFUND_FILTER_MODALITY,
	CONSULT_REFUND_FILTER_STATUS,
	DEFAULT_CONSULT_REFUND_V2_FILTERS,
} from '../filterConstants';

const {all, applyFilters, filter, filters} = translations['pt-br'].commons;
const {clearFilterButton} = translations['pt-br'].privateFlow.myReferrals;
const {
	filterModalityLabel,
	filterStatusLabel,
	modality: refundModalityLabels,
	status: refundStatusLabels,
} = translations['pt-br'].privateFlow.consultRefund;
const {cards: modalityCards} = translations['pt-br'].privateFlow.refund.serviceType;

export function useConsultRefundFilterDrawer() {
	const {
		activeRefundFilterCount,
		clearConsultRefundFilters,
		filterModality,
		filterStatus,
		setConsultRefundFilters,
	} = useConsultRefundV2();

	const [openDrawerState, setOpenDrawerState] = useState(false);
	const [localStatus, setLocalStatus] = useState(filterStatus);
	const [localModality, setLocalModality] = useState(filterModality);

	const statusOptions = useMemo(
		() => [
			{category: CONSULT_REFUND_FILTER_STATUS.ALL, label: all},
			{category: CONSULT_REFUND_FILTER_STATUS.RECEIVED, label: refundStatusLabels.registered},
			{category: CONSULT_REFUND_FILTER_STATUS.CANCELED, label: refundStatusLabels.cancelled},
			{category: CONSULT_REFUND_FILTER_STATUS.DENIED, label: refundStatusLabels.denied},
			{category: CONSULT_REFUND_FILTER_STATUS.IN_ANALYSIS, label: refundStatusLabels.analysis},
			{category: CONSULT_REFUND_FILTER_STATUS.APPROVED, label: refundStatusLabels.approved},
			{category: CONSULT_REFUND_FILTER_STATUS.CONCLUDED, label: refundStatusLabels.concluded},
		],
		[
			all,
			refundStatusLabels.analysis,
			refundStatusLabels.approved,
			refundStatusLabels.cancelled,
			refundStatusLabels.concluded,
			refundStatusLabels.denied,
			refundStatusLabels.registered,
		],
	);

	const modalityOptions = useMemo(
		() => [
			{category: CONSULT_REFUND_FILTER_MODALITY.ALL, label: all},
			{category: CONSULT_REFUND_FILTER_MODALITY.CONSULT, label: modalityCards.consult},
			{
				category: CONSULT_REFUND_FILTER_MODALITY.SPECIAL_EXAM,
				label: refundModalityLabels.specialExam,
			},
			{
				category: CONSULT_REFUND_FILTER_MODALITY.SIMPLE_EXAM,
				label: refundModalityLabels.simpleExam,
			},
			{
				category: CONSULT_REFUND_FILTER_MODALITY.HOSPITALIZATION,
				label: modalityCards.hospitalization,
			},
			{
				category: CONSULT_REFUND_FILTER_MODALITY.EMERGENCY,
				label: modalityCards.emergencyRoom,
			},
			{category: CONSULT_REFUND_FILTER_MODALITY.THERAPY, label: modalityCards.therapy},
		],
		[
			all,
			modalityCards.consult,
			modalityCards.emergencyRoom,
			modalityCards.hospitalization,
			modalityCards.therapy,
			refundModalityLabels.simpleExam,
			refundModalityLabels.specialExam,
		],
	);

	const listFilters = useMemo(
		() => [
			{
				categoryTitle: filterStatusLabel,
				categoryItemsList: statusOptions.map(option => ({
					active: localStatus === option.category,
					title: option.label,
					onClick: () => setLocalStatus(option.category),
				})),
			},
			{
				categoryTitle: filterModalityLabel,
				categoryItemsList: modalityOptions.map(option => ({
					active: localModality === option.category,
					title: option.label,
					onClick: () => setLocalModality(option.category),
				})),
			},
		],
		[filterModalityLabel, filterStatusLabel, localModality, localStatus, modalityOptions, statusOptions],
	);

	const handleClearFilters = () => {
		setLocalStatus(DEFAULT_CONSULT_REFUND_V2_FILTERS.status);
		setLocalModality(DEFAULT_CONSULT_REFUND_V2_FILTERS.modality);
		clearConsultRefundFilters();
		setOpenDrawerState(false);
	};

	const handleSubmitFilters = () => {
		setConsultRefundFilters({status: localStatus, modality: localModality});
		setOpenDrawerState(false);
	};

	useEffect(() => {
		setLocalStatus(filterStatus);
		setLocalModality(filterModality);
	}, [filterModality, filterStatus]);

	return {
		activeRefundFilterCount,
		applyFilters,
		clearFilterButton,
		filter,
		filters,
		handleClearFilters,
		handleSubmitFilters,
		listFilters,
		openDrawerState,
		setOpenDrawerState,
	};
}
